<template>
  <div class="campaign-holder">
    <div
      v-if="!setupVal(campaignId) && !enabled(campaignId) && !affiliateView"
      data-cy="rtb-disabled-section"
    >
      <SectionHeader
        :show-content="dynamicInboundCallsSettingsShowContents"
        :widget-title="$t('HEADER_DYNAMIC_INBOUND_CALLS')"
        @onShowContentsClick="onDynamicInboundCallsSettingsShowContentsClick"
      >
        <template #content>
          <form
            role="form"
            class="form-horizontal"
            name="dynamicInboundCallsSettings"
          >
            <div class="bs-callout bs-callout-sm bs-callout-primary m-10">
              <h2 v-t="'RTB_INTRO'" />
            </div>
            <Field
              :label="$t('LABEL_ENABLED')"
              :hide-label="false"
              :has-tooltip="true"
              :tooltip-text="$t('TOOLTIP_ENABLED')"
            >
              <template #element>
                <nzToggle
                  data-cy="rtb-disabled-toggle"
                  @onToggle="onEnabledToggle"
                />
              </template>
            </Field>
          </form>
        </template>
      </SectionHeader>
    </div>

    <div
      v-if="!setupVal(campaignId) && affiliateView"
      class="bs-callout bs-callout-sm bs-callout-primary"
    >
      <h2 v-t="'RTB_INTRO_FOR_DISABLED_PUB'" />
    </div>

    <div
      v-if="setupVal(campaignId) || enabled(campaignId)"
      data-cy="rtb-enabled-section"
    >
      <ValidationObserver
        ref="formValidation"
        v-slot="{ pristine, handleSubmit }"
        slim
      >
        <form @submit.prevent="handleSubmit(onSaveAll)">
          <DynamicInboundCallsSettings
            :campaign-id="campaignId"
            :affiliate="affiliate"
            :affiliate-view-rtb-id="affiliateView ? affiliateViewRtbId(campaignId) : ''"
            :affiliate-view="affiliateView"
            :overrides-available="overridesAvailable"
            :override-dynamic-inbound-calls-settings="campaignSettingsOverride(campaignId, affiliateId)"
            :enabled="enabled(campaignId, affiliateId)"
            :campaginIdAndAffiliateId="campaginIdAndAffiliateId"
            :sip-only="sipOnly(campaignId, affiliateId)"
            :caller-id-required="callerIdRequired(campaignId, affiliateId)"
            :icp="icp(campaignId, affiliateId)"
            :journaya-tcpa="journayaTcpa(campaignId, affiliateId)"
            :trusted-form="trustedForm(campaignId, affiliateId)"
            :trusted-form-integration="trustedFormIntegration(campaignId, affiliateId)"
            :trusted-form-settings="trustedFormSettings(campaignId, affiliateId)"
            :jornaya-settings="jornayaSettings(campaignId, affiliateId)"
            :callCaps="selectedCallCaps"
            :payout-capacity="payoutCapacity(campaignId, affiliateId)"
            @overrideDynamicInboundCallsSettingsToggle="onCampaignSettingsOverrideToggle"
            @onEnabledToggle="onEnabledToggle"
            @onOnlySipToggle="onOnlySipToggle"
            @onCallerIdRequiredToggle="onCallerIdRequiredToggle"
            @onIcpChange="onIcpChange"
            @onJournayaTcpaChange="onJournayaTcpaChange"
            @onTrustedFormChange="onTrustedFormChange"
            @onTrustedFormIntegrationChange="onTrustedFormIntegrationChange"
            @onTrustedFormSettingsChange="onTrustedFormSettingsChange"
            @onJornayaSettingsChange="onJornayaSettingsChange"
            @onPayoutCapsChange="onPayoutCapsChange"
            @onCallCapsChange="onCallCapsChange"
          />

          <div v-if="enabled(campaignId, affiliateId)">
            <BidDurationSettings
              :affiliate-view="affiliateView"
              :overrides-available="overridesAvailable"
              :bid-expiration-in-seconds="bidExpirationInSeconds(campaignId, affiliateId)"
              :route-calls-for-stale-bids="routeCallsForStaleBids(campaignId, affiliateId)"
              :caller-id-required="callerIdRequired(campaignId, affiliateId)"
              @onBidGoodForOnInput="onBidGoodForOnInput"
              @onBidGoodForOnChange="onBidGoodForOnChange"
              @onRouteCallsForStaleBidsChange="onRouteCallsForStaleBidsChange"
            />

            <RateLimit
              :affiliate-view="affiliateView"
              :overrides-available="overridesAvailable"
              :override-rate-limit-settings="rateLimitOverride(campaignId, affiliateId)"
              :max-requests="maxRequests(campaignId, affiliateId)"
              :per-seconds="perSeconds(campaignId, affiliateId)"
              :per-seconds-dropdown-config="perSecondsDropdownConfig"
              @overrideRateLimitSettingsToggle="onRateLimitOverrideToggle"
              @onMaximumRequestsOnInput="onMaximumRequestsOnInput"
              @onMaximumRequestsOnChange="onMaximumRequestsOnChange"
              @onPerSecondsDropdownOnChange="onPerSecondsDropdownOnChange"
            />

            <DefaultBidSettings
              v-if="!affiliateView"
              :pass-through-bid-feature-flag="passThroughBidFeatureFlag"
              :overrides-available="overridesAvailable"
              :current-pass-through-bid="passThroughBid(campaignId, affiliateId)"
              :override-default-bid-settings="bidSettingsOverride(campaignId, affiliateId)"
              :base-bid-amount="baseBidAmount(campaignId, affiliateId)"
              :max-bid-amount="maxBidAmount(campaignId, affiliateId)"
              :min-bid-amount="minBidAmount(campaignId, affiliateId)"
              :conversion-type="conversionType(campaignId, affiliateId)"
              :conversion-type-dropdown-config="conversionTypeDropdownConfig"
              :start-timer-event="startTimerEvent(campaignId, affiliateId)"
              :call-length-in-seconds="callLengthInSeconds(campaignId, affiliateId)"
              :payout-hours-toggleval="payoutHoursToggleval"
              :seconds-from-last-call="secondsFromLastCall(campaignId, affiliateId)"
              :limit-payout-toggleval="limitPayoutToggleval"
              @overrideDefaultBidSettingsToggle="onBidSettingsOverrideToggle"
              @onBaseBidAmountOnInput="onBaseBidAmountOnInput"
              @onBaseBidAmountOnChange="onBaseBidAmountOnChange"
              @onmaxBidAmountOnInput="onmaxBidAmountOnInput"
              @onmaxBidAmountOnChange="onmaxBidAmountOnChange"
              @onminBidAmountOnInput="onminBidAmountOnInput"
              @onminBidAmountOnChange="onminBidAmountOnChange"
              @onPayoutOnDropdownOnChange="onPayoutOnDropdownOnChange"
              @onStartTimerEventChange="onStartTimerEventChange"
              @onCallLengthInSecondsOnInput="onCallLengthInSecondsOnInput"
              @onCallLengthInSecondsOnChange="onCallLengthInSecondsOnChange"
              @onPayoutHoursToggle="onPayoutHoursToggle"
              @onSecondsFromLastCallChange="onSecondsFromLastCallChange"
              @onLimitPayoutToggle="onLimitPayoutToggle"
            />

            <DefaultPassThroughBid
              v-if="!affiliateView && passThroughBidFeatureFlag"
              :overrides-available="overridesAvailable"
              :current-pass-through-bid="passThroughBid(campaignId, affiliateId)"
              :override-default-pass-through-bid="passThroughBidOverride(campaignId, affiliateId)"
              :current-pass-through-bid-override="passThroughBidOverrideValues(campaignId, affiliateId)"
              :current-min-duration-adjustments="minDurationAdjustments(campaignId, affiliateId)"
              :current-min-duration-adjustments-override="minDurationAdjustmentsOverride(campaignId, affiliateId)"
              @updatePassThroughBid="onUpdatePassThroughBid"
              @enablePassThroughBidToggle="onEnablePassThroughBidToggle"
              @overrideDefaultPassThroughBidToggle="onPassThroughBidOverrideToggle"
              @updateMinDuration="onMinDurationAdjustmentChange"
            />

            <CustomScoringSettings
              v-if="!affiliateView && !affiliateId"
              :has-custom-scoring-v1="hasCustomScoringV1(campaignId, affiliateId)"
              :current-custom-scoring="customScoring(campaignId, affiliateId)"
              :custom-scoring-modified="customScoringModified(campaignId, affiliateId)"
              :save-triggered="customScoringUpdatedSuccess"
              @updateCustomScoringModified="onUpdateCustomScoringModified"
              @updateCustomScoringSettings="onUpdateCustomScoringSettings"
              @resetCustomScoringUpdatedSuccess="onResetCustomScoringUpdatedSuccess"
            />

            <BidAdjustments
              v-if="!affiliateView"
              :overrides-available="overridesAvailable"
              :override-default-bid-settings="overrideDefaultBidSettings"
              :override-bid-adjustments="bidAdjustmentsOverride(campaignId, affiliateId)"
              :rows="rows(campaignId, affiliateId)"
              :bulk-modifiers="bulkModifiers"
              :current-row="currentRow"
              :last-row-id="lastRowId"
              :edit-row="editRow"
              :headers="headers"
              :show-add-adjustment-button="showAddAdjustmentButton"
              :adjustments-changed="adjustmentsChanged"
              :adjustment-panel-properties="{ availableTags, recentlyUsedTags, valuesForFilter, comparisonTypes, operationTypes }"
              :hide-save-and-cancel="hideSaveAndCancel"
              @overrideBidAdjustmentsToggle="onBidAdjustmentsOverrideToggle"
              @onAddAdjustment="onAddAdjustment"
              @onEditAdjustment="onEditAdjustment"
              @onAddAdjustmentButtonClick="onAddAdjustmentButtonClick"
              @onResetAdjustments="onResetAdjustments"
              @onSaveAdjustments="onSaveAll"
              @onCancelAdjustments="onCancelAdjustments"
              @onOrderChange="onOrderChange"
              @drag="onDrag"
              @onSetCurrentRow="onSetCurrentRow"
              @onToggleEditRow="onToggleEditRow"
              @onShowRowAdjustment="onShowRowAdjustment"
              @onRemoveRow="onRemoveRow"
            />
          </div>

          <div
            v-if="!affiliateView && !hideSaveAndCancel"
            class="save-btns-dynamic-inbound-calls col-sm-offset-2 col-md-offset-3 m-b-25"
          >
            <button
              v-t="'BUTTON_SAVE'"
              name="rtb-form-save-button"
              data-cy="rtb-form-save-button"
              :disabled="(showAddAdjustmentButton || affiliateView || (pristine && !adjustmentsChanged)) && setupVal(campaignId)"
              class="btn btn-primary bid-adjustment-btn m-t-20 m-l-20" 
              type="submit"
            />
          </div>
        </form>
      </ValidationObserver>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import messages from '../locales';
import sharedMessages from '@/core/locales';

import { cloneDeep, isEqual, sortBy } from 'lodash';
import { camelCaseFormat } from '@/core/filters/camelCaseFormat';

import DynamicInboundCallsSettings from '../DynamicInboundCallsSettings/DynamicInboundCallsSettings';
import BidDurationSettings from '../BidDurationSettings/BidDurationSettings';
import RateLimit from '../RateLimit/RateLimit';
import DefaultBidSettings from '../DefaultBidSettings/DefaultBidSettings';
import DefaultPassThroughBid from '../DefaultPassThroughBid/DefaultPassThroughBid';
import CustomScoringSettings from '../CustomScoringSettings/CustomScoringSettings';
import BidAdjustments from '../BidAdjustments/BidAdjustments';
import SectionHeader from '@/core/components/SectionHeader/SectionHeader';
import Field from '@/core/components/LabelAndElement/LabelAndElement';
import nzToggle from '@/core/components/nzToggle/nzToggle';

import { utilities } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { bidAdjustmentsComparisonTypes, bidAdjustmentsOperationTypes } from '@/data/filterData';
import { availableTags } from '@/data/filterData';
import { valuesForFilters } from '@/mock/mock-reporting-filter-data';

const sortedBidAdjustmentsComparisonTypes = sortBy(bidAdjustmentsComparisonTypes, [function(b) { return b.value; }]);

const perSecondsDropdownConfig = () => {
  return {
    options: ['Per Minute', 'Per Hour', 'Per Day'],
    placeholder: 'Per Minute'
  };
};

const conversionTypeDropdownConfig = () => {
  return {
    options: ['Call Length', 'Call Successfully Connected', 'Inbound Call'],
    placeholder: 'Inbound Call'
  };
};

export default {
  name: 'DynamicInboundCalls',
  components: {
    DynamicInboundCallsSettings,
    BidDurationSettings,
    RateLimit,
    DefaultBidSettings,
    DefaultPassThroughBid,
    CustomScoringSettings,
    BidAdjustments,
    SectionHeader,
    Field,
    nzToggle
  },
  props: {
    campaignId: {
      type: String,
      default: () => {
        return '';
      }
    },
    affiliateId: {
      type: String,
      default: () => {
        return '';
      }
    },
    overridesAvailable: {
      type: Boolean,
      default: () => {
        return false;
      }
    },
    hideSaveAndCancel: {
      type: Boolean,
      default: () => {
        return false;
      }
    },
    affiliateView: {
      type: Boolean,
      default: () => {
        return false;
      }
    }
  },
  data() {
    return {
      lastRowId: '',
      affiliate: { id: '', showDynamicInboundCallsDocumentationModal: false },
      //TODORTBASAP all possible data consts
      payoutHoursToggleval: true,
      limitPayoutToggleval: true,
      bidAdjustmentsShowContents: true,
      //const
      headers: [
        {name: '#', property: 'priority'},
        {name: 'Tag', property: 'tag'},
        {name: 'Logic', property: 'comparer'},
        {name: 'Value', property: 'compvalue'},
        {name: 'Operation', property: 'operation'},
        {name: 'Value', property: 'opvalue'},
        {name: 'Actions', property: 'actions' }],
      availableTags: [...availableTags],
      recentlyUsedTags: [
        { tagType: 'InboundNumber', tagName: 'ProvinceCode', tagSource: 'Call' },
        { tagType: 'wer', tagValue: '', tagName: 'eee', tagSource: 'User' }
      ],
      valuesForFilter: valuesForFilters,
      comparisonTypes: [...sortedBidAdjustmentsComparisonTypes],
      operationTypes: [...bidAdjustmentsOperationTypes],
      adjustmentPanelProperties: null,
      showAddAdjustmentButton: false,
      adjustmentsChanged: false,
      currentRow: null,
      editRow: false,
      rowOptions: this.adjustmentPanelProperties,
      overrideDefaultBidSettings: true,
      dynamicInboundCallsSettingsShowContents: true,
      customScoringUpdatedSuccess: false,
      callCaps: {},
    };
  },
  computed:{
    ...mapState({
      featureFlags: 'currentAccountFeatureFlags'
    }),
    ...mapState('dynamicInboundCalls', ['bulkModifiers']),
    ...mapGetters('dynamicInboundCalls', [
      'setupVal',
      'campaignSettingsOverride',
      'affiliateViewRtbId',
      'enabled',
      'sipOnly',
      'payoutCapacity',
      'isCapGlobal',
      'callerIdRequired',
      'icp',
      'journayaTcpa',
      'trustedForm',
      'trustedFormIntegration',
      'trustedFormSettings',
      'jornayaSettings',
      'bidExpirationInSeconds',
      'routeCallsForStaleBids',
      'rateLimitOverride',
      'maxRequests',
      'perSeconds',
      'bidSettingsOverride',
      'passThroughBid',
      'passThroughBidOverride',
      'passThroughBidOverrideValues',
      'minDurationAdjustments',
      'minDurationAdjustmentsOverride',
      'customScoring',
      'customScoringModified',
      'hasCustomScoringV1',
      'bidAdjustmentsOverride',
      'baseBidAmount',
      'maxBidAmount',
      'minBidAmount',
      'conversionType',
      'startTimerEvent',
      'callLengthInSeconds',
      'secondsFromLastCall',
      'bidAdjustments',
      'rows',
      'rowsPreviousState'
    ]),
    perSecondsDropdownConfig,
    conversionTypeDropdownConfig,
    passThroughBidFeatureFlag() {
      return this.featureFlags.passthrough;
    },
    campaginIdAndAffiliateId() {
      return this.campaignId + this.affiliateId;
    },
    selectedCallCaps: {
      get: function() {
        return this.callCaps[this.campaginIdAndAffiliateId] ?? {}
      },
      set: function(val) {
        const newCallCaps = cloneDeep(this.callCaps)
        newCallCaps[this.campaginIdAndAffiliateId] = val
        this.callCaps = newCallCaps
      }
    }
  },
  created() {
    this.adjustmentPanelProperties = {
      'availableTags': this.availableTags,
      'recentlyUsedTags': this.recentlyUsedTags,
      'valuesForFilter': this.valuesForFilter,
      'comparisonTypes': this.comparisonTypes,
      'operationTypes': this.operationTypes,
    };
    
    this.getMacros();
    if (!this.affiliateView) {
      this.getBulkAdjustmentLists();
    }
  },
  methods: {
    ...mapActions('dynamicInboundCalls', [
      'pullDynamicInboundCallsCampaignSettings',
      'pullRTBAllPubSettings',
      'pushSetupDynamicInboundCallsCampaignSettings',
      'pushDynamicInboundCallsCampaignSettings',
      'pushDynamicInboundCallsPublisherSettings',
      'setSetupVal',
      'setCampaignSettingsOverride',
      'setEnabled',
      'setPayoutCapacity',
      'setSipOnly',
      'setCallerIdRequired',
      'setIcp',
      'setJournayaTcpa',
      'setTrustedForm',
      'setTrustedFormIntegration',
      'setTrustedFormSettings',
      'setJornayaSettings',
      'setBidExpirationInSeconds',
      'setRouteCallsForStaleBids',
      'setRateLimitOverride',
      'setMaxRequests',
      'setPerSeconds',
      'setBidSettingsOverride',
      'setPassThroughBid',
      'setPassThroughBidOverride',
      'setBidAdjustmentsOverride',
      'setCustomScoringSettings',
      'setCustomScoringModified',
      'setBaseBidAmount',
      'setmaxBidAmount',
      'setminBidAmount',
      'setConversionType',
      'setStartTimerEvent',
      'setCallLengthInSeconds',
      'setSecondsFromLastCall',
      'setMinDurationAdjustments',
      'saveBidAdjustments',
      'setRows',
      'setRowsPreviousState',
      'getMacros',
      'getBulkAdjustmentLists',
      'resetPayoutCapacity',
    ]),
    onShowRowAdjustment(row){
      this.$emit('onShowRowAdjustment', row);
      this.$emit('onAddAdjustmentButtonClick', !this.showAddAdjustmentButton);
      this.currentRow = row;
      this.editRow = true;
      this.rowOptions = this.adjustmentPanelProperties,
      this.rowOptions.adjustment = {
        tagId: row.tag,
        comparisonTypeTemp: {value: row.comparer},
        comparisonValue: row.compValue,
        operationTypeTemp: {value: row.operation},
        operationValue: row.opValue,
        bulkListId: row.bulkListId,
        overrideTag: row.overrideTag,
      };
    },
    onCampaignSettingsOverrideToggle(val) {
      if (this.affiliateId) {
        this.setCampaignSettingsOverride(this._getCampaignIdAndAffiliateId(val));
      }
    },
    onEnabledToggle(val) {
      if (this.affiliateView) {
        return;
      }
      this.setEnabled(this._getCampaignIdAndAffiliateId(val));
    },
    onPayoutCapsChange(val) {
      if (this.affiliateView) {
        return;
      }
      this.setPayoutCapacity(this._getCampaignIdAndAffiliateId(val));
    },
    onCallCapsChange(val) {
      if (this.affiliateView) {
        return;
      }
      this.selectedCallCaps = val;
    },
    onOnlySipToggle(val) {
      if (this.affiliateView) {
        return;
      }
      this.setSipOnly(this._getCampaignIdAndAffiliateId(val));
    },
    onCallerIdRequiredToggle(val) {
      if (this.affiliateView) {
        return;
      }
      this.setCallerIdRequired(this._getCampaignIdAndAffiliateId(val));
    },
    onIcpChange(val) {
      if (this.affiliateView) {
        return;
      }
      this.setIcp(this._getCampaignIdAndAffiliateId(val.value));
    },
    onJournayaTcpaChange(val) {
      if (this.affiliateView) {
        return;
      }
      this.setJournayaTcpa(this._getCampaignIdAndAffiliateId(val.value));
    },
    onJornayaSettingsChange(val) {
      if (this.affiliateView) {
        return;
      }
      this.setJornayaSettings(this._getCampaignIdAndAffiliateId(val.value));
    },
    onTrustedFormChange(val) {
      if (this.affiliateView) {
        return;
      }
      this.setTrustedForm(this._getCampaignIdAndAffiliateId(val.value));
    },
    onTrustedFormIntegrationChange(val) {
      if (this.affiliateView) {
        return;
      }
      this.setTrustedFormIntegration(this._getCampaignIdAndAffiliateId(val.value));
    },
    onTrustedFormSettingsChange(val) {
      if (this.affiliateView) {
        return;
      }
      this.setTrustedFormSettings(this._getCampaignIdAndAffiliateId(val.value));
    },
    onBidGoodForOnInput(val) {
      if (this.affiliateView) {
        return;
      }
      this.setBidExpirationInSeconds(this._getCampaignIdAndAffiliateId(val.target.value));
    },
    onBidGoodForOnChange(val) {
      if (this.affiliateView) {
        return;
      }
      this.setBidExpirationInSeconds(this._getCampaignIdAndAffiliateId(val.target.value));
    },
    onRouteCallsForStaleBidsChange(val) {
      if (this.affiliateView) {
        return;
      }
      this.setRouteCallsForStaleBids(this._getCampaignIdAndAffiliateId(val.name != 'Reject'));
    },
    onRateLimitOverrideToggle(val) {
      if (this.affiliateView) {
        return;
      }
      if (this.affiliateId) {
        this.setRateLimitOverride(this._getCampaignIdAndAffiliateId(val));
      }
    },
    onMaximumRequestsOnInput(val) {
      if (this.affiliateView) {
        return;
      }
      this.setMaxRequests(this._getCampaignIdAndAffiliateId(val.target.value));
    },
    onMaximumRequestsOnChange(val) {
      if (this.affiliateView) {
        return;
      }
      this.setMaxRequests(this._getCampaignIdAndAffiliateId(val.target.value));
    },
    onPerSecondsDropdownOnChange(val) {
      if (this.affiliateView) {
        return;
      }
      this.setPerSeconds(this._getCampaignIdAndAffiliateId(val));
    },
    onBidSettingsOverrideToggle(val) {
      if (this.affiliateView) {
        return;
      }
      if (this.affiliateId) {
        this.setBidSettingsOverride(this._getCampaignIdAndAffiliateId(val));
      }
    },
    onBaseBidAmountOnInput(val) {
      if (this.affiliateView) {
        return;
      }
      this.setBaseBidAmount(this._getCampaignIdAndAffiliateId(val.target.value));
    },
    onBaseBidAmountOnChange(val) {
      if (this.affiliateView) {
        return;
      }
      this.setBaseBidAmount(this._getCampaignIdAndAffiliateId(val.target.value));
    },
    onmaxBidAmountOnInput(val) {
      if (this.affiliateView) {
        return;
      }
      this.setmaxBidAmount(this._getCampaignIdAndAffiliateId(val.target.value));
    },
    onmaxBidAmountOnChange(val) {
      if (this.affiliateView) {
        return;
      }
      this.setmaxBidAmount(this._getCampaignIdAndAffiliateId(val.target.value));
    },
    onminBidAmountOnInput(val) {
      if (this.affiliateView) {
        return;
      }
      this.setminBidAmount(this._getCampaignIdAndAffiliateId(val.target.value));
    },
    onminBidAmountOnChange(val) {
      if (this.affiliateView) {
        return;
      }
      this.setminBidAmount(this._getCampaignIdAndAffiliateId(val.target.value));
    },
    onPayoutOnDropdownOnChange(val) {
      if (this.affiliateView) {
        return;
      }
      this.setConversionType(this._getCampaignIdAndAffiliateId(val));
    },
    onStartTimerEventChange(val) {
      if (this.affiliateView) {
        return;
      }
      this.setStartTimerEvent(this._getCampaignIdAndAffiliateId(val.value));
    },
    onCallLengthInSecondsOnInput(val) {
      if (this.affiliateView) {
        return;
      }
      this.setCallLengthInSeconds(this._getCampaignIdAndAffiliateId(val.target.value));
    },
    onCallLengthInSecondsOnChange(val) {
      if (this.affiliateView) {
        return;
      }
      this.setCallLengthInSeconds(this._getCampaignIdAndAffiliateId(val.target.value));
    },
    onSecondsFromLastCallChange(val){
      if (this.affiliateView) {
        return;
      }
      this.setSecondsFromLastCall(this._getCampaignIdAndAffiliateId(val));
    },
    onPayoutHoursToggle(val){
      if (this.affiliateView) {
        return;
      }
      this.payoutHoursToggleval = val;
      this.$emit('payoutHoursToggle', val);
    },
    onLimitPayoutToggle(val){
      if (this.affiliateView) {
        return;
      }
      this.limitPayoutToggleval = val;
      this.$emit('limitPayoutToggle', val);
    },
    // Set pass through
    onEnablePassThroughBidToggle(val) {
      const passThroughBid = {
        enabled: val,
        allowBidsAtLoss: false
      };
      if (this.affiliateView) {
        return;
      }
      this.setPassThroughBid(this._getCampaignIdAndAffiliateId(passThroughBid));

      // If Pass Through is disabled, null out the minDurationAdjustments, they are only used when Pass Through is enabled.
      if (!val) {
        this.setMinDurationAdjustments(this._getCampaignIdAndAffiliateId(null));
      }
    },
    onUpdatePassThroughBid(passThroughBid) {
      this.setPassThroughBid(this._getCampaignIdAndAffiliateId(passThroughBid));
    },
    onMinDurationAdjustmentChange(minDurationAdjustmentChange) {
      this.setMinDurationAdjustments(this._getCampaignIdAndAffiliateId(minDurationAdjustmentChange));
    },
    onPassThroughBidOverrideToggle(val) {
      if (this.affiliateView) {
        return;
      }
      if (this.affiliateId) {
        this.setPassThroughBidOverride(this._getCampaignIdAndAffiliateId(val));
      }
    },
    // end pass through
    onUpdateCustomScoringSettings(customScoringSettings) {
      this.$refs.formValidation.flags.pristine = false;
      this.setCustomScoringSettings(this._getCampaignIdAndAffiliateId(customScoringSettings));
    },
    onUpdateCustomScoringModified(val) {
      this.setCustomScoringModified(this._getCampaignIdAndAffiliateId(val));
    },
    onResetCustomScoringUpdatedSuccess() {
      this.customScoringUpdatedSuccess = false;
    },
    onBidAdjustmentsOverrideToggle(val) {
      if (this.affiliateView) {
        return;
      }
      if (this.affiliateId) {
        this.setBidAdjustmentsOverride(this._getCampaignIdAndAffiliateId(val));
      }
    },
    onBidAdjustmentsShowContentsClick() {
      if (this.affiliateView) {
        return;
      }
      this.bidAdjustmentsShowContents = !this.bidAdjustmentsShowContents;
    },
    onDrag(){
      this.$emit('onDrag');
    },
    onSetCurrentRow(val) {
      this.currentRow = val;
      this.$emit('onSetCurrentRow', val);
    },
    onToggleEditRow(val) {
      this.editRow = val;
      this.$emit('onToggleEditRow', val);
    },
    onOrderChange(unorderedRowsAndOldAndNewIndexes) {
      var reorderedRows = this.move(unorderedRowsAndOldAndNewIndexes[0], unorderedRowsAndOldAndNewIndexes[1], unorderedRowsAndOldAndNewIndexes[2]);
      this.setRows(this._getCampaignIdAndAffiliateId(reorderedRows));
      this.adjustmentsChanged = true;
      this.$emit('onOrderChange', reorderedRows);
    },
    move(items, oldIndex, newIndex) {
      const itemRemovedArray = [
        ...items.slice(0, oldIndex),
        ...items.slice(oldIndex + 1, items.length)
      ];

      var newItem = JSON.parse(JSON.stringify(items[oldIndex]));

      var result = [
        ...itemRemovedArray.slice(0, newIndex),
        newItem,
        ...itemRemovedArray.slice(newIndex, itemRemovedArray.length)
      ];

      var startChangeIndex = oldIndex > newIndex ? newIndex : oldIndex,
        endChangeIndex = oldIndex > newIndex ? oldIndex : newIndex;

      for (var i = startChangeIndex; i <= endChangeIndex; i++) {
        result[i].priority = i;
      }

      return result;
    },
    sanitizeRow(row){
      var newRow = JSON.parse(JSON.stringify(row));
      delete newRow.id;
      delete newRow.showAddAdjustmentButton;
      newRow.priority+=1;

      return newRow;
    },
    onAddAdjustmentButtonClick(val) {
      this.showAddAdjustmentButton = val;
      this.$emit('onAddAdjustmentButtonClick', val);
    },
    onAddAdjustment(newAdjustment) {
      this.addNewRow(newAdjustment);
      this.adjustmentsChanged = true;
    },
    onEditAdjustment(editedAdjustment) {
      const temp = cloneDeep(this.rows(this.campaignId, this.affiliateId));

      if (isEqual(temp[editedAdjustment.priority], editedAdjustment)) {
        return;
      }

      temp[editedAdjustment.priority] = editedAdjustment;
      this.setRows(this._getCampaignIdAndAffiliateId(temp));

      this.adjustmentsChanged = true;
    },
    onResetAdjustments() {
      if (this.rows(this.campaignId, this.affiliateId).length == 0) { return; }
      this.setRows(this._getCampaignIdAndAffiliateId([]));
      this.adjustmentsChanged = true;
    },
    onSaveAdjustments() {
      if (this.affiliateView) {
        return;
      }
      this.setRowsPreviousState(cloneDeep(this._getCampaignIdAndAffiliateId(this.rows(this.campaignId, this.affiliateId))));
      this.adjustmentsChanged = false;
      this.saveBidAdjustments(cloneDeep(this._getCampaignIdAndAffiliateId(this.rowsPreviousState(this.campaignId, this.affiliateId))));
    },
    onCancelAdjustments() {
      this.setRows(this._getCampaignIdAndAffiliateId(cloneDeep(this.rowsPreviousState(this.campaignId, this.affiliateId))));
      this.adjustmentsChanged = false;
    },
    addNewRow(newRow) {
      let temp = cloneDeep(this.rows(this.campaignId, this.affiliateId));
      temp = temp || [];
      const id = Date.now() + temp.length;
      temp.push(
        {
          id,
          priority: temp.length,
          // TODO: remove next line when we get back to search
          tag: newRow.tag,
          // TODO: uncomment next line when we get back to search
          //tag: !tagText ? newRow.tag : tagText,
          comparer: camelCaseFormat(newRow.comparer),
          compValue: newRow.compValue,
          operation: camelCaseFormat(newRow.operation),
          bulkListId: newRow.bulkListId,
          overrideTag: newRow.overrideTag,
          opValue: newRow.opValue
        });

      this.setRows(this._getCampaignIdAndAffiliateId(temp));

      this.lastRowId = id.toString();
    },
    onRemoveRow(row) {
      var result = cloneDeep(this.rows(this.campaignId, this.affiliateId));
      result.splice(row.priority, 1);

      for (let i = row.priority; i < result.length; i++) {
        result[i].priority = result[i].priority - 1;
      }

      this.setRows(this._getCampaignIdAndAffiliateId(result));
      this.adjustmentsChanged = true;
    },
    async onSaveAll() {
      let toasterMessage = 'RTB settings saved successfully';
      const enablingRtbForTheFirstTime = this.setupVal(this.campaignId) === false && this.enabled(this.campaignId) === true;

      if (enablingRtbForTheFirstTime) {
        this.setSetupVal(this._getCampaignIdAndAffiliateId(true));
        toasterMessage = 'RTB Enabled on campaign and settings saved successfully';
      }

      this.setRowsPreviousState(cloneDeep(this._getCampaignIdAndAffiliateId(this.rows(this.campaignId, this.affiliateId))));
      this.adjustmentsChanged = false;
      this.saveBidAdjustments(cloneDeep(this._getCampaignIdAndAffiliateId(this.rowsPreviousState(this.campaignId, this.affiliateId))));

      try {
        if (this.affiliateId) {
          await this.pushDynamicInboundCallsPublisherSettings({ ...this._getCampaignIdAndAffiliateId(), callCaps: this.selectedCallCaps });
        } else if (enablingRtbForTheFirstTime) {
          await this.pushSetupDynamicInboundCallsCampaignSettings(this.campaignId);
        } else {
          await this.pushDynamicInboundCallsCampaignSettings(this.campaignId);
        }
        this.customScoringUpdatedSuccess = true;
        utilities.showToaster('success', toasterMessage);
      } catch (e) {
        
        utilities.errorHandler(e);
      }
    },
    onDynamicInboundCallsSettingsShowContentsClick() {
      this.dynamicInboundCallsSettingsShowContents = !this.dynamicInboundCallsSettingsShowContents;
    },
    _getCampaignIdAndAffiliateId(val) {
      return {
        campaignId: this.campaignId,
        affiliateId: this.affiliateId,
        val: cloneDeep(val),
      };
    },
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  },
};
</script>